import { useQuery } from '@tanstack/react-query';

import { ExchangeRateData } from '@/features/exchange';
import { ICurrencyAccount } from '@/entities/currency-account';
import { useCompanyIdStore } from '@/entities/user';
import {
  VabsArRateChartPeriod,
  requestsClient,
} from '@/shared/api';

interface ExchangeRateChartPoint {
  date: string;
  rate: number;
}

interface ExchangeRatePair {
  id: string;
  isReversed: boolean;
}

interface UseExchangeRatesParams {
  fromCurrency?: ICurrencyAccount | null;
  toCurrency?: ICurrencyAccount | null;
  chartPeriod?: VabsArRateChartPeriod;
  assetPair?: ExchangeRatePair | null;
}

const parseRateValue = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const sortRatesByDate = (rates: ExchangeRateChartPoint[]) => {
  return [...rates].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
};

const getExchangeRatePair = async ({
  fromCurrency,
  toCurrency,
}: UseExchangeRatesParams): Promise<ExchangeRatePair | null> => {
  const fromCode = fromCurrency?.code?.trim().toUpperCase();
  const toCode = toCurrency?.code?.trim().toUpperCase();
  if (!fromCode || !toCode || fromCode === toCode) return null;

  const directPairTicker = `${fromCode}/${toCode}`;
  const reversePairTicker = `${toCode}/${fromCode}`;

  const { vabsArAssetPairs } = await requestsClient.vabsArAssetPairs({
    input: {
      pairTickers: directPairTicker,
      pagination: {
        limit: 20,
        skip: 0,
      },
    },
  });

  const pair =
    vabsArAssetPairs.nodes.find(
      (item) => item.pairTicker === directPairTicker,
    ) ??
    vabsArAssetPairs.nodes.find((item) => item.pairTicker === reversePairTicker);

  if (!pair) return null;

  return {
    id: pair.id,
    isReversed: pair.pairTicker === reversePairTicker,
  };
};

const getFallbackDate = (
  baseDate: string,
  chartPeriod?: VabsArRateChartPeriod,
) => {
  const parsedDate = new Date(baseDate);
  const daysToSubtract = (() => {
    switch (chartPeriod) {
      case VabsArRateChartPeriod.OneDay:
        return 1;
      case VabsArRateChartPeriod.OneWeek:
        return 7;
      case VabsArRateChartPeriod.OneMonth:
        return 30;
      case VabsArRateChartPeriod.SixMonths:
        return 180;
      case VabsArRateChartPeriod.OneYear:
        return 365;
      default:
        return 180;
    }
  })();

  parsedDate.setDate(parsedDate.getDate() - daysToSubtract);
  return parsedDate.toISOString();
};

const getExchangeRates = async ({
  fromCurrency,
  toCurrency,
  chartPeriod = VabsArRateChartPeriod.All,
  companyId,
  assetPair,
}: UseExchangeRatesParams & { companyId: string }): Promise<ExchangeRateData[]> => {
  const fromCode = fromCurrency?.code?.trim().toUpperCase();
  const toCode = toCurrency?.code?.trim().toUpperCase();
  if (!fromCode || !toCode || fromCode === toCode) return [];

  const pair = assetPair ?? (await getExchangeRatePair({ fromCurrency, toCurrency }));
  if (!pair) return [];

  const { vabsArPersonalRateCharts } = await requestsClient.vabsArPersonalRateCharts({
    input: {
      assetPairId: pair.id,
      clientId: companyId,
      chartPeriod,
    },
  });

  const points = vabsArPersonalRateCharts.points.length
    ? vabsArPersonalRateCharts.points
    : vabsArPersonalRateCharts.lastPoint
      ? [vabsArPersonalRateCharts.lastPoint]
      : [];

  const rates = points.reduce<ExchangeRateChartPoint[]>((acc, point) => {
    const rate = parseRateValue(point.rateValue);
    if (!rate || !Number.isFinite(rate)) return acc;
    const normalizedRate = pair.isReversed ? 1 / rate : rate;
    if (!Number.isFinite(normalizedRate) || normalizedRate <= 0) return acc;
    return [...acc, { date: point.date, rate: normalizedRate }];
  }, []);

  const sortedRates = sortRatesByDate(rates);
  if (sortedRates.length !== 1) return sortedRates;

  const [singleRate] = sortedRates;

  return [
    {
      ...singleRate,
      date: getFallbackDate(singleRate.date, chartPeriod),
    },
    singleRate,
  ];
};

export const useExchangeRates = ({
  fromCurrency,
  toCurrency,
  chartPeriod,
  assetPair,
}: UseExchangeRatesParams) => {
  const companyId = useCompanyIdStore((state) => state.companyId);
  const pairLookupDisabled = Boolean(assetPair);
  const enabled =
    Boolean(companyId) &&
    Boolean(fromCurrency?.code) &&
    Boolean(toCurrency?.code) &&
    fromCurrency?.code !== toCurrency?.code &&
    (pairLookupDisabled ? Boolean(assetPair?.id) : true);

  return useQuery({
    queryKey: [
      'exchange-rates',
      pairLookupDisabled ? assetPair?.id : fromCurrency?.code,
      pairLookupDisabled
        ? assetPair?.isReversed
          ? 'reversed'
          : 'direct'
        : toCurrency?.code,
      chartPeriod ?? VabsArRateChartPeriod.All,
      companyId,
    ],
    enabled,
    queryFn: () =>
      getExchangeRates({
        fromCurrency,
        toCurrency,
        chartPeriod,
        assetPair,
        companyId: companyId || '',
      }),
  });
};
